import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core'
import {AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl} from '@angular/forms'
import {Cart} from "@spartacus/cart/base/root";
import {BehaviorSubject, combineLatest, Observable, Subscription} from 'rxjs'
import {filter, switchMap} from "rxjs/operators";
import {EbModalComponent} from '../../../../components/modal/eb-modal.component'
import {EbAnalyticsService} from "../../../analytics/services/facades/eb-analytics.service";
import {EbCartModification} from '../../interface/eb-cart.interface'
import {EbQuickOrderEntry} from '../../interface/eb-order-entry.interface'
import {EbActiveCartService} from '../../services/eb-active-cart.service'
import {EbMultiCartService} from '../../services/eb-multi-cart.service'

@Component({
    selector: 'eb-quick-order-form',
    templateUrl: './eb-quick-order-form.component.html',
    styleUrls: ['./eb-quick-order-form.component.scss'],
    standalone: false
})
export class EbQuickOrderFormComponent implements OnInit, OnDestroy {
  maxQuantity = 1000
  quickOrderForm = this.formBuilder.group({
    quickOrderItems: this.formBuilder.array([])
  })
  loading$ = new BehaviorSubject(false)
  cartEntries$: Observable<Cart>;
  private subscriptions = new Subscription()
  @ViewChild('modal') modal: EbModalComponent
  quickOrderArticleNumbers: string[] = []

  constructor(
    private activeCartService: EbActiveCartService,
    private multiCartService: EbMultiCartService,
    protected formBuilder: UntypedFormBuilder,
    private analyticsService : EbAnalyticsService,
  ) {
    this.cartEntries$ = activeCartService.getActive()
  }

  ngOnInit() {
    this.addQuickOrderItem()

    this.subscriptions.add(
      this.multiCartService.filterByAddedEntriesError().subscribe(erroneousEntries => {
        this.rebuildForm(erroneousEntries)
        this.loading$.next(false)
      })
    )
  }

  get quickOrderItems() {
    // tslint:disable-next-line:no-string-literal
    return this.quickOrderForm.controls.quickOrderItems as UntypedFormArray
  }

  addQuickOrderItem(code = '', quantity = 1, error?): void {
    const quickOrderForm = this.formBuilder.group({
      articleNumber: code,
      quantity,
      error
    })
    if (error) {
      quickOrderForm.get('articleNumber').markAsTouched()
      quickOrderForm.get('articleNumber').markAsDirty()
    }
    this.quickOrderItems.push(this.convertToFormControl(quickOrderForm))
  }

  isAnyArticleNumberValid() {
    return this.quickOrderItems.controls.some(formGroup => formGroup.value.articleNumber !== '')
  }

  addToCart() {
    const quickOrderEntries = this.quickOrderItems.controls
      .map(quickOrderItem => {
        if (quickOrderItem.value.articleNumber !== '') {
          return {
            quantity: quickOrderItem.value.quantity,
            product: { code: quickOrderItem.value.articleNumber },
            isQuickAdd: true
          } as EbQuickOrderEntry
        }
      })
      .filter(value => value)
    this.activeCartService.addQuickOrderEntries(quickOrderEntries)
    this.loading$.next(true)
    this.quickOrderArticleNumbers = this.quickOrderItems.controls.map(quickOrderItem => quickOrderItem.value.articleNumber).filter(value => value);
    this.updateAnalyticsOnAddEntryToCart()
  }

  updateAnalyticsOnAddEntryToCart(){
    this.subscriptions.add(
      this.activeCartService.getLoading().pipe(
        filter(isLoading => !isLoading),
        switchMap(() => this.activeCartService.getEntries())
      ).subscribe(cartEntries => {
        this.analyticsService.addEcommerceProductToCart(cartEntries, '','Ecommerce', 'Quick-add-to-cart', 'Line item');
      })
    );
  }

  rebuildForm(erroneousEntries: EbCartModification[]) {
    if (erroneousEntries?.length) {
      this.quickOrderItems.clear()
      erroneousEntries.forEach(value =>
        this.addQuickOrderItem(value.entry.product.code, value.entry.quantity, value.statusCode)
      )
    } else {
      this.quickOrderItems.clear()
      this.addQuickOrderItem()
      this.triggerQuickOrderEntryEvent()
    }
  }

  convertToFormControl(abstractControl: AbstractControl | null): UntypedFormControl {
    return abstractControl as UntypedFormControl
  }

  getErrorMessageKey(value: any): string {
    return `quickOrder.${value}`
  }

  triggerQuickOrderEntryEvent() {
      this.subscriptions.add(
        combineLatest([this.activeCartService.isStable(),this.activeCartService.getEntries()])
          .pipe(filter(([isStable]) => isStable))
          .subscribe(([,entries]) => {
            this.quickOrderArticleNumbers.filter((articleNumber) => {
              entries.filter((entry) => {
                if(entry.product?.code === articleNumber) {
                  this.analyticsService.addQuickOrderEntries(entry)
                }
              })
            }
          )
          this.quickOrderArticleNumbers = []
          })
      )
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe()
  }
}
